import { Component, computed, effect, input, signal, ChangeDetectionStrategy } from "@angular/core";
import { TranslateModule } from "@ngx-translate/core";
import { ProgressSpinnerModule } from "@openng/optimus-ui/progressspinner";
import { GATE_POSITION_THRESHOLDS } from "../../../constants/gatePositionThresholds";
import { DateTime } from "luxon";
import { createTimeoutSignal } from "../../utils/timeout.utils";
import { TimeoutComponent } from "../../components/timeout/timeout.component";
import { DurationPoint } from "../../../models/ui/durationPoint";

@Component({
  standalone: true,
  selector: "app-open-duration",
  imports: [TranslateModule, ProgressSpinnerModule, TimeoutComponent],
  templateUrl: "./open-duration.component.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: "./open-duration.component.scss",
})
export class OpenDurationComponent {
  public noData = computed(
    () =>
      this.gatePosition() === undefined &&
      this.gatePositionTimestamp() === undefined &&
      this.longestOpenDuration() === undefined &&
      this.lastOpenDuration() === undefined
  );

  public loading = computed(
    () =>
      this.gatePosition() === null ||
      this.gatePositionTimestamp() === null ||
      this.longestOpenDuration() === null ||
      this.lastOpenDuration() === null
  );
  public timeOut = createTimeoutSignal(this.loading);

  public gatePosition = input<number | null>();
  public gatePositionTimestamp = input<number | null>();

  public longestOpenDuration = input<number | null>();
  public lastOpenDuration = input<number | null>();

  public displayLongestOpenDuration = computed(() => {
    const longestOpenDuration = this.longestOpenDuration();
    if (longestOpenDuration === null || longestOpenDuration === undefined) return null;
    return this.convertToDurationPoint(longestOpenDuration);
  });
  public displayLastOpenDuration = computed(() => {
    const lastOpenDuration = this.lastOpenDuration();
    if (lastOpenDuration === null || lastOpenDuration === undefined) return null;
    return this.convertToDurationPoint(lastOpenDuration);
  });

  public timerDisplay = signal<{ value: string; unit: string } | null>(null);

  private interval: ReturnType<typeof setInterval> | undefined;

  constructor() {
    effect(() => {
      if (this.interval !== undefined) {
        clearInterval(this.interval);
        this.interval = undefined;
      }
      const gatePosition = this.gatePosition();
      if (gatePosition === null || gatePosition === undefined) {
        return;
      }
      if (gatePosition >= GATE_POSITION_THRESHOLDS.CLOSED) {
        const gatePositionTimestamp = this.gatePositionTimestamp();
        if (gatePositionTimestamp === null || gatePositionTimestamp === undefined) {
          return;
        }
        const currentOpenTime = DateTime.now().toMillis() - gatePositionTimestamp;
        this.timerDisplay.set(this.convertToDurationPoint(currentOpenTime));
        this.interval = setInterval(() => {
          const currentOpenTime = DateTime.now().toMillis() - gatePositionTimestamp;
          this.timerDisplay.set(this.convertToDurationPoint(currentOpenTime));
        }, 1000);
      } else {
        this.timerDisplay.set({
          value: "gate_position_widget.closed",
          unit: "",
        });
      }
    });
  }

  /**
   * Converts the given open duration in milliseconds to a DurationPoint.
   *
   * @param openDurationMs The open duration in milliseconds.
   * @returns A DurationPoint containing the formatted duration and unit.
   */
  private convertToDurationPoint(openDurationMs: number): DurationPoint {
    return {
      value: this.formatMilisecondsForDisplay(openDurationMs),
      unit: this.getUnit(openDurationMs),
    };
  }
  /**
   * Format the given open duration in milliseconds for display.
   * @param miliseconds The open duration in milliseconds.
   * @returns A string representing the formatted duration.
   * If the duration is:
   * - < 1 minute, show seconds.
   * - < 1 hour, show minutes and seconds.
   * - < 1 day, show hours and minutes.
   * - >= 1 day, show days.
   * If the duration is 0 or negative, show "--".
   */
  private formatMilisecondsForDisplay(miliseconds: number): string {
    //const duration = moment.duration(miliseconds);
    const ONE_MINUTE = 60 * 1000;
    const ONE_HOUR = ONE_MINUTE * 60;
    const ONE_DAY = ONE_HOUR * 24;
    let formatedValue;
    if (miliseconds > 0) {
      const convertedMili = this.convertMiliseconds(miliseconds);
      if (miliseconds < ONE_MINUTE) {
        formatedValue = convertedMili.s.toString();
      } else if (miliseconds < ONE_HOUR) {
        formatedValue = convertedMili.m.toString() + ":" + convertedMili.s.toString();
      } else if (miliseconds < ONE_DAY) {
        formatedValue = convertedMili.h.toString() + ":" + convertedMili.m.toString();
      } else {
        formatedValue = convertedMili.d.toString();
      }
    } else {
      formatedValue = "--";
    }

    return formatedValue;
  }

  /**
   * Converts the given open duration in milliseconds to a duration in days, hours, minutes and seconds.
   * @param miliseconds The open duration in milliseconds.
   * @returns An object with the following properties:
   * - d: the number of days (padded to 2 digits)
   * - h: the number of hours (padded to 2 digits)
   * - m: the number of minutes (padded to 2 digits)
   * - s: the number of seconds (padded to 2 digits)
   */
  private convertMiliseconds(miliseconds: number) {
    let hours, minutes, seconds;

    const total_seconds = Number(Math.floor(miliseconds / 1000));
    const total_minutes = Number(Math.floor(total_seconds / 60));
    const total_hours = Number(Math.floor(total_minutes / 60));
    const days = Number(Math.floor(total_hours / 24));

    seconds = Number(total_seconds % 60);
    minutes = Number(total_minutes % 60);
    hours = Number(total_hours % 24);

    seconds = seconds < 10 ? "0" + seconds.toString() : seconds.toString();
    minutes = minutes < 10 ? "0" + minutes.toString() : minutes.toString();
    hours = hours < 10 ? "0" + hours.toString() : hours.toString();

    return {
      d: days,
      h: hours,
      m: minutes,
      s: seconds,
    };
  }

  /**
   * Given a duration in milliseconds, returns the appropriate unit of time.
   * The method uses the following rules:
   * - 0 milliseconds: returns an empty string
   * - milliseconds < 1 minute: returns "units.seconds_abbr"
   * - milliseconds < 1 hour: returns "units.minutes_abbr"
   * - milliseconds < 1 day: returns "units.hours_abbr"
   * - milliseconds >= 1 day and < 2 days: returns "units.day"
   * - milliseconds >= 2 days: returns "units.days"
   * @param miliseconds The duration in milliseconds.
   * @returns The unit of time as a string.
   */
  private getUnit(miliseconds: number): string {
    const ONE_MINUTE = 60 * 1000;
    const ONE_HOUR = ONE_MINUTE * 60;
    const ONE_DAY = ONE_HOUR * 24;
    let unit;
    if (miliseconds === 0) {
      unit = "";
    } else if (miliseconds < ONE_MINUTE) {
      unit = "units.seconds_abbr";
    } else if (miliseconds < ONE_HOUR) {
      unit = "units.minutes_abbr";
    } else if (miliseconds < ONE_DAY) {
      unit = "units.hours_abbr";
    } else if (miliseconds >= ONE_DAY && miliseconds < ONE_DAY * 2) {
      unit = "units.day";
    } else {
      unit = "units.days";
    }
    return unit;
  }
}
